// src/Pages/AdminBranches.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Chip,
  Fab,
  Snackbar,
  Alert,
  Card,
  Avatar,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import BranchForm from "./BranchForm";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useSnackbar } from "../Snackbar/SnackbarContext";
import axios from "axios";

import apiEndpoints from "../../apiconfig";

const AdminBranches = () => {
  const [branches, setBranches] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const { showSnackbar } = useSnackbar();
  const token = localStorage.getItem("token");

  // ⭐ Fetch branches from backend
  const fetchBranches = async () => {
    try {
      const res = await axios.get(apiEndpoints.branches, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.status === "success") {
        setBranches(res.data.data);
      }
    } catch (error) {
      console.error("Fetch branches error:", error);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const handleMenuOpen = (event, branch) => {
    setAnchorEl(event.currentTarget);
    setEditingBranch(branch); // attach selected branch
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // ⭐ CREATE / UPDATE BRANCH
  const handleSubmit = async (formData) => {
    try {
      if (editingBranch && !viewMode) {
        // UPDATE Branch
        const res = await axios.put(
          `${apiEndpoints.branches}?guid=${editingBranch.guid}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (res.data.status === "success") {
          fetchBranches();
          showSnackbar("Branch updated successfully!", "success");
        }
      } else {
        // CREATE Branch (NO guid sent)
        const res = await axios.post(apiEndpoints.branches, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data.status === "success") {
          fetchBranches();
          showSnackbar("Branch created successfully!", "success");
        }
      }
    } catch (error) {
      console.error("Branch save error:", error);
      showSnackbar("Failed to save branch", "error");
    }

    setOpenForm(false);
    setEditingBranch(null);
    setViewMode(false);
  };

  // ⭐ DELETE branch
  const handleDelete = async (guid) => {
    if (!window.confirm("Are you sure to delete this branch?")) return;

    try {
      const res = await axios.delete(`${apiEndpoints.branches}?guid=${guid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.status === "success") {
        fetchBranches();
        showSnackbar("Branch deleted successfully!", "error");
      }
    } catch (error) {
      console.error("Delete error:", error);
      showSnackbar("Failed to delete branch", "error");
    }
  };

  return (
    <>
      <Container
        maxWidth="xl"
        sx={{
          py: 4,
          background: "rgba(255, 255, 255, 0.25)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.4)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box
          sx={{
            mb: 4,
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* LEFT: Title + Subtitle */}
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Branches
            </Typography>
            <Typography sx={{ mb: 0, color: "text.secondary" }}>
              Manage school branches and operational details
            </Typography>
          </Box>

          {/* RIGHT: Add Button */}
          <Fab
            color="primary"
            size="medium"
            sx={{
              boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
            }}
            onClick={() => {
              setEditingBranch(null); // <-- update based on your code variable
              setOpenForm(true);
            }}
          >
            <Add />
          </Fab>
        </Box>

        <Grid container spacing={3}>
          {branches.map((branch) => (
            <Grid
              item
              sx={{ width: { xs: "100%", sm: "50%", md: "50%", lg: "33.3%", xl: "33.3%" } }}
              key={branch.guid}
            >
              <Card
                onClick={() => {
                  setEditingBranch(branch);
                  setViewMode(true);
                  setOpenForm(true);
                }}
                sx={{
                  p: 3,
                  borderRadius: "14px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  height: "140px", // ← adjust if needed
                }}
              >
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <Avatar src={branch.logo} sx={{ width: 60, height: 60 }} />

                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {branch.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {branch.city}, {branch.state}
                    </Typography>

                    <Chip
                      label={branch.isActive ? "Active" : "Inactive"}
                      size="small"
                      sx={{
                        mt: 1,
                        bgcolor: branch.isActive
                          ? "rgba(76,175,80,0.12)"
                          : "rgba(244,67,54,0.12)",
                        color: branch.isActive ? "green" : "red",
                      }}
                    />
                  </Box>
                </Box>

                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e, branch);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Menu: Edit/Delete */}
        <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
          <MenuItem
            onClick={() => {
              setViewMode(false);
              setOpenForm(true);
              handleMenuClose();
            }}
          >
            <Edit sx={{ mr: 1 }} /> Edit
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleDelete(editingBranch.guid);
              handleMenuClose();
            }}
          >
            <Delete sx={{ mr: 1 }} color="error" /> Delete
          </MenuItem>
        </Menu>
      </Container>

      {openForm && (
        <BranchForm
          initialData={editingBranch}
          viewOnly={viewMode}
          onSubmit={handleSubmit}
          onClose={() => {
            setOpenForm(false);
            setEditingBranch(null);
            setViewMode(false);
          }}
        />
      )}
    </>
  );
};

export default AdminBranches;
